import { BRAND_GREEN, GERMAN_ARTICLES, LANE_COLORS, PIXEL_FONT, WORD_FONT } from "./render";

// Sharing a run, built for posting on Reddit: a Wordle-style text block for
// comments, a square pixel-art card for an image post, and a link that unfurls
// into "Can you beat 340?".
//
// Everything here takes the finished run plus which game it was: { score,
// correct, bestStreak, history, articles, title }.

// The lane colours as squares: blue masculine, purple feminine, yellow neuter.
const EMOJI = { das: "🟨", der: "🟦", die: "🟪", la: "🟪", le: "🟦", miss: "💥" };
const EMOJI_PER_LINE = 10;
const MAX_EMOJI = 40;

export function challengeUrl(shareUrl, score) {
  const url = new URL(shareUrl, window.location.origin);
  url.searchParams.set("challenge", score);
  return url.toString();
}

// The run as coloured squares, one per gate: the article's colour for a right
// answer, a crash for a wrong one. Long runs keep their opening and say how
// many more there were, so the block stays postable.
export function emojiGrid(history) {
  const shown = history.slice(0, MAX_EMOJI).map((entry) => EMOJI[entry]);
  const lines = [];
  for (let i = 0; i < shown.length; i += EMOJI_PER_LINE) {
    lines.push(shown.slice(i, i + EMOJI_PER_LINE).join(""));
  }
  if (history.length > MAX_EMOJI) lines.push(`+${history.length - MAX_EMOJI} more`);
  return lines.join("\n");
}

const DEFAULT_TITLE = "Der Die Das Runner";

export function shareTitle(result) {
  return `I scored ${result.score} on ${result.title || DEFAULT_TITLE} - can you beat it?`;
}

export function shareText(result, url) {
  return [
    `${result.title || DEFAULT_TITLE} 🏃 ${result.score}`,
    emojiGrid(result.history),
    `${result.correct} right · ${result.bestStreak} in a row`,
    url,
  ].join("\n");
}

export function redditUrl(result, url) {
  return `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(shareTitle(result))}`;
}

export function xUrl(result, url) {
  return `https://x.com/intent/post?text=${encodeURIComponent(shareTitle(result))}&url=${encodeURIComponent(url)}`;
}

// --- The score card -------------------------------------------------------
//
// Laid out on a 270x270 grid and drawn straight onto a 1080x1080 canvas under a
// 4x transform: the blocks land on exact 4px pixels, and the text is rendered
// at full size rather than blown up. (Drawing at 270 and scaling the bitmap up
// turned every anti-aliased letter edge into a blur.) Square, which is what an
// image post on Reddit or Instagram crops to anyway.

const CARD = 270;
const CARD_SCALE = 4;
const SQUARE_COLORS = { ...LANE_COLORS, miss: "#e5484d" };

function drawCard(ctx, result) {
  ctx.fillStyle = "#160f3a";
  ctx.fillRect(0, 0, CARD, CARD);
  ["#211650", "#341d61", "#4f236b"].forEach((color, i) => {
    ctx.fillStyle = color;
    ctx.fillRect(0, 70 + i * 8, CARD, 8);
  });
  ctx.fillStyle = "#1d2029";
  ctx.fillRect(0, 94, CARD, CARD - 94);

  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  ctx.fillStyle = BRAND_GREEN;
  ctx.font = `9px ${PIXEL_FONT}`;
  ctx.fillText("CLOZEMASTER LABS", CARD / 2, 14);

  // The articles in the serif, like everywhere else in the game.
  ctx.font = `700 20px ${WORD_FONT}`;
  const articles = result.articles || GERMAN_ARTICLES;
  const middle = (articles.length - 1) / 2;
  articles.forEach((article, i) => {
    ctx.fillStyle = LANE_COLORS[article];
    ctx.fillText(article, CARD / 2 + (i - middle) * 56, 28);
  });
  ctx.fillStyle = "#fdf6e3";
  ctx.font = `11px ${PIXEL_FONT}`;
  ctx.fillText("RUNNER", CARD / 2, 52);

  ctx.font = `34px ${PIXEL_FONT}`;
  ctx.fillStyle = "#fdf6e3";
  ctx.fillText(String(result.score), CARD / 2, 104);

  ctx.font = `8px ${PIXEL_FONT}`;
  ctx.fillStyle = "#b9c1cc";
  ctx.fillText(`${result.correct} RIGHT  ·  ${result.bestStreak} IN A ROW`, CARD / 2, 146);

  // The same squares as the text block.
  const shown = result.history.slice(0, MAX_EMOJI);
  const size = 12;
  const gap = 3;
  const rowWidth = EMOJI_PER_LINE * (size + gap) - gap;
  shown.forEach((entry, i) => {
    const col = i % EMOJI_PER_LINE;
    const row = Math.floor(i / EMOJI_PER_LINE);
    const x = (CARD - rowWidth) / 2 + col * (size + gap);
    const y = 166 + row * (size + gap);
    ctx.fillStyle = SQUARE_COLORS[entry];
    ctx.fillRect(x, y, size, size);
    if (entry === "miss") {
      ctx.fillStyle = "#1d2029";
      for (let d = 2; d < size - 2; d += 1) {
        ctx.fillRect(x + d, y + d, 1, 1);
        ctx.fillRect(x + size - 1 - d, y + d, 1, 1);
      }
    }
  });

  ctx.fillStyle = BRAND_GREEN;
  ctx.fillRect(0, CARD - 24, CARD, 24);
  ctx.fillStyle = "#14161f";
  ctx.font = `8px ${PIXEL_FONT}`;
  ctx.fillText("CAN YOU BEAT IT?  CLOZEMASTER.COM", CARD / 2, CARD - 16);
}

export function scoreCardBlob(result) {
  const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

  return fontsReady.then(
    () =>
      new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        canvas.width = CARD * CARD_SCALE;
        canvas.height = CARD * CARD_SCALE;
        const ctx = canvas.getContext("2d");
        ctx.setTransform(CARD_SCALE, 0, 0, CARD_SCALE, 0, 0);
        drawCard(ctx, result);
        canvas.toBlob(resolve, "image/png");
      })
  );
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
